import { useState } from "react";
import { Link } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { parkWorkPending, listWorkPending, listWorkSyncErrors, listWorkReceiptCandidates, retryWorkPending, dismissWorkPending, compensateWorkPending, verifyWorkPending } from "../../api/hub/work";

function ParkUnknown({ id, busy, park }: { id: string; busy: boolean; park: (reason: string) => void }) {
  const [reason, setReason] = useState("");
  return <form onSubmit={e => { e.preventDefault(); park(reason.trim()); }}>
    <p>暂时无法取得回执时，可人工挂起。结果仍未知，保留全部原记录与核验入口，不能据此重发或关闭。</p>
    <label>挂起原因<input aria-label={`${id} 挂起原因`} required maxLength={500} value={reason} onChange={e => setReason(e.target.value)}/></label>
    <button disabled={busy || !reason.trim()}>挂起并保留未知记录</button>
  </form>;
}
const resourceKind = (kind: string) => ({ task: "任务", tasklist: "任务清单", section: "清单分组", custom_field: "自定义字段" }[kind] ?? "远端资源");
function ReceiptPicker({ id, busy, verify }: { id: string; busy: boolean; verify: (guid?: string) => void }) {
  const candidates = useQuery({ queryKey: ["hub", "work-receipt-candidates", id], queryFn: () => listWorkReceiptCandidates(id), enabled: false });
  const [selected, setSelected] = useState(""), [guid, setGuid] = useState("");
  return <>
    <p>结果未知，请先查找飞书中的实际资源。选择候选后，系统仍会读取远端回执并核对原请求；不一致时继续待核查。</p>
    <button disabled={busy || candidates.isFetching} onClick={() => { setSelected(""); void candidates.refetch(); }}>查找飞书回执候选</button>
    {candidates.isFetching && <p role="status">正在读取飞书资源…</p>}
    {candidates.isError && <p role="alert">{candidates.error.message}</p>}
    {candidates.data && <>
      {candidates.data.guidance && <p>{candidates.data.guidance}</p>}
      {candidates.data.candidates.length ? <label>实际飞书资源
        <select aria-label={`选择 ${id} 的飞书资源`} value={selected} onChange={e => { setSelected(e.target.value); setGuid(""); }}>
          <option value="">请选择需要核对的资源</option>
          {candidates.data.candidates.map(c => <option key={c.guid} value={c.guid}>{resourceKind(c.kind)} · {c.title || "无标题资源"}{c.createdAt ? ` · ${new Date(c.createdAt).toLocaleString("zh-CN")}` : ""} · {c.guid}</option>)}
        </select>
      </label> : <p>未找到可选择的回执。保留待核查记录，按上方指引核对。</p>}
    </>}
    <details><summary>手动输入飞书资源编号（GUID）</summary>
      <label>飞书资源 GUID<input aria-label={`核对 ${id} 的飞书资源 GUID`} value={guid}
        onChange={e => { setGuid(e.target.value); setSelected(""); }} /></label>
    </details>
    <button disabled={busy} onClick={() => verify(selected || guid.trim() || undefined)}>核对远端回执</button>
  </>;
}

export function WorkSyncPanel() {
  const queryClient = useQueryClient();
  const pending = useQuery({ queryKey: ["hub", "work-pending"], queryFn: listWorkPending, refetchInterval: 30_000 });
  const syncErrors = useQuery({ queryKey: ["hub", "work-sync-errors"], queryFn: listWorkSyncErrors, refetchInterval: 30_000 });
  const [error, setError] = useState(""), [busy, setBusy] = useState("");
  const run = async (id: string, fn: () => Promise<unknown>) => {
    setBusy(id); setError("");
    try { await fn(); await queryClient.invalidateQueries({ queryKey: ["hub"] }); }
    catch (e) { setError((e as Error).message); }
    finally { setBusy(""); }
  };
  if (!pending.data?.length && !syncErrors.data?.length && !error && !pending.isError && !syncErrors.isError) return null;
  return <aside className="work-sync-panel" aria-label="工作同步待处理">
    <h2>同步待处理</h2>
    {error && <p role="alert">{error}</p>}
    {pending.isError && <p role="alert">待处理记录读取失败：{pending.error.message}</p>}
    {syncErrors.isError && <p role="alert">同步错误读取失败：{syncErrors.error.message}</p>}
    {!!syncErrors.data?.length && <section aria-label="对账错误">
      <h3>飞书对账错误</h3>
      <p>相关事项可能尚未更新。请先核对飞书授权与来源权限；修正后等待下一轮对账。</p>
      {syncErrors.data.map((e, i) => <article key={`${e.projectId ?? ""}:${e.taskGuid ?? ""}:${i}`}>
        <p>{e.message}</p>
        <p className="work-meta">状态：{e.status}{e.taskGuid ? ` · 飞书任务：${e.taskGuid}` : ""}</p>
        {e.projectId && <Link to={`/projects?project=${encodeURIComponent(e.projectId)}`}>打开相关项目</Link>}
      </article>)}
    </section>}
    {pending.data?.map(p => <article key={p.id}>
      {p.manualDisposition?.active && <><p>已人工挂起 · 结果仍未知</p><p>挂起原因：{p.manualDisposition.reason}</p></>}
      <details open={!p.manualDisposition?.active}><summary>{p.manualDisposition?.active ? "展开核验与恢复操作" : "核验与恢复操作"}</summary>
      <p>{p.error || "飞书同步暂未完成"}</p>
      {!(p.kind.endsWith(".create") && p.status === "rejected") &&
        <Link to={p.kind.startsWith("task.") ? `/tasks?task=${encodeURIComponent(p.entityId)}` : `/projects?project=${encodeURIComponent(p.entityId)}`}>打开事项并修改</Link>}
      {p.status === "rejected" ? <>
        <p>飞书已明确拒绝失败步骤。{p.kind.endsWith(".create") ?
          p.hasRemoteReceipt ? "远端已有资源，可补偿撤销。" : "未留下远端创建回执，可关闭失败记录。" :
          "可以修正事项后重新保存；已完成的前序变化仍保留。"}</p>
        {p.kind.endsWith(".create") && p.hasRemoteReceipt ?
          <button disabled={busy === p.id} onClick={() => void run(p.id, () => compensateWorkPending(p.id))}>补偿撤销远端资源</button> :
          <button disabled={busy === p.id} onClick={() => void run(p.id, () => dismissWorkPending(p.id))}>关闭失败记录</button>}
      </> : p.status === "needs_review" ? <>
        <ReceiptPicker id={p.id} busy={busy === p.id} verify={guid => void run(p.id, () => verifyWorkPending(p.id, guid))} />
        {p.hasRemoteReceipt && p.canCompensate !== false && ["task.create", "project.create"].includes(p.kind) && <><p>已有远端创建回执。确认不再保留资源时，可按原创建身份尝试补偿；结果不明时仍需继续核验。</p><button disabled={busy === p.id} onClick={() => void run(p.id, () => compensateWorkPending(p.id))}>补偿撤销远端资源</button></>}
        {p.canCompensate === false && <p>请先核对当前未知步骤，或挂起保留记录；已有父资源回执不能证明未知子步骤已撤销。</p>}
        {!p.manualDisposition?.active && <ParkUnknown id={p.id} busy={busy === p.id} park={reason => void run(p.id, () => parkWorkPending(p.id, reason))}/>}
      </> : <>
        {p.status === "awaiting_auth" && <p>请先重新授权飞书或修正应用权限，再重试。</p>}
        <button disabled={busy === p.id || p.status === "sending"} onClick={() => void run(p.id, () => retryWorkPending(p.id))}>重试同步</button>
      </>}
      </details>
    </article>)}
  </aside>;
}
